import { Typography } from 'antd';
import { Link } from 'react-router-dom';
import { ItemImage, OrganisationAvatar } from '../../../components';
import { MuteText } from '../../../elements';
import { ProductPropertiesConverted } from '../../shipments/common';
import { CommonUtility, DateFormat, DateUtility } from '../../../utility';
import { InventoryLocationTags, InventoryLotLocationTags } from './InventoryLocationTags';
import { ProductThumb, RowActionsCell } from './inventoryListStyled';
import { ProductTitleEllipsis } from './ProductTitleEllipsis';
import {
  InventoryLotRowActions,
  InventoryProductRowActions,
} from './InventoryRowActions';
import type {
  InventoryListApiItem,
  InventoryListColumnKey,
  InventoryLotRow,
  LocationSummary,
} from './inventoryListTypes';

const { Text } = Typography;

export type InventoryListCellContext = {
  imperial: boolean;
  openPopup: (
    item: InventoryListApiItem & { lotIndex: number; transferLot?: boolean },
  ) => void;
  editItem: (item: InventoryListApiItem) => void;
  editBarcodes: (item: InventoryListApiItem) => void;
  editRestock: (item: InventoryListApiItem) => void;
  openItemPopupLogs: (item: InventoryListApiItem) => void;
  editLotAttribute: (lot: LocationSummary & { itemId?: string }) => void;
  editPallet: (payload: { lot: LocationSummary } & InventoryListApiItem) => void;
  onDeleteLot: (lot: LocationSummary) => void;
};

function renderQty(value: number | undefined) {
  if (value === undefined || value === null) return null;
  return (
    <Text style={{ fontSize: 14, display: 'block', textAlign: 'right' }}>
      {CommonUtility.numberWithCommas(value)}
    </Text>
  );
}

export function renderInventoryProductCell(
  key: InventoryListColumnKey,
  item: InventoryListApiItem,
  ctx: InventoryListCellContext,
) {
  switch (key) {
    case 'description':
      return (
        <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
          <ProductThumb>
            <ItemImage
              imageKey="p_images"
              displayImageKey="display_image"
              item={item.product}
              size="mini"
              noMargin
            />
          </ProductThumb>
          <div className="ml-4" style={{ minWidth: 0, flex: 1 }}>
            <ProductTitleEllipsis title={item.product?.p_title} />
            <div className="d-flex">
              <MuteText className="mr-2 text-nowrap">SKU</MuteText>
              <Text strong copyable>
                {item.product?.p_sku}
              </Text>
            </div>
            <div className="d-flex mt-2">
              <OrganisationAvatar
                name={item?.customerOrganization?.company_name}
                image={item?.customerOrganization?.logo?.url}
                mini
              />
              &nbsp;{item?.customerOrganization?.company_name}
            </div>
          </div>
        </div>
      );
    case 'location':
      return <InventoryLocationTags locationSummary={item.locationSummary} />;
    case 'carton_configurations':
      return (
        <ProductPropertiesConverted
          attributes={{
            ...(item.product?.package_dimensions || {}),
            alias: item.product?.p_sku,
          }}
          unitKeys
          imperial={ctx.imperial}
        />
      );
    case 'on_hand':
      return renderQty(item.quantity?.available);
    case 'transfer':
      return renderQty(item.quantity?.transfer);
    case 'allocated':
      return renderQty(item.quantity?.allocated);
    case 'total':
      return renderQty(item.quantity?.total);
    case 'actions':
      return (
        <RowActionsCell>
          <InventoryProductRowActions
            item={item}
            openPopup={ctx.openPopup}
            editItem={ctx.editItem}
            editBarcodes={ctx.editBarcodes}
            editRestock={ctx.editRestock}
            openItemPopupLogs={ctx.openItemPopupLogs}
          />
        </RowActionsCell>
      );
    default:
      return null;
  }
}

export function renderInventoryLotCell(
  key: InventoryListColumnKey,
  row: InventoryLotRow,
  ctx: InventoryListCellContext,
) {
  const { item, lot, lotIndex } = row;

  switch (key) {
    case 'description':
      return (
        <div className="d-flex justify-content-between">
          <div>
            <MuteText>Lot #</MuteText>
            <Link
              to={`/app/inventory/item/${lot.inventoryId}`}
              className="ml-2"
              onClick={e => e.stopPropagation()}
            >
              {lot.lotNumber || 'N/A'}
            </Link>
          </div>
          {lot.expiryDate && (
            <div className="d-flex mr-4">
              <MuteText>Expiry</MuteText>
              <span className="ml-2">
                {DateUtility.formatDate(lot.expiryDate, DateFormat.barChartMonth)}
              </span>
            </div>
          )}
        </div>
      );
    case 'location':
      return <InventoryLotLocationTags lot={lot} />;
    case 'carton_configurations':
      return (
        <div className="d-flex">
          <MuteText className="text-nowrap">Alias</MuteText>
          <span className="ml-2">{lot?.cartonAttributes?.alias || '-'}</span>
        </div>
      );
    case 'on_hand':
      return renderQty(lot.quantity?.available);
    case 'transfer':
      return renderQty(lot.quantity?.transfer);
    case 'allocated':
      return renderQty(lot.quantity?.allocated);
    case 'total':
      return renderQty(lot.quantity?.total);
    case 'actions':
      return (
        <RowActionsCell>
          <InventoryLotRowActions
            parentItem={item}
            lot={lot}
            lotIndex={lotIndex}
            openPopup={ctx.openPopup}
            editLotAttribute={ctx.editLotAttribute}
            editPallet={ctx.editPallet}
            onDeleteLot={ctx.onDeleteLot}
          />
        </RowActionsCell>
      );
    default:
      return null;
  }
}
